import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, KeyRound, Loader2, RefreshCw, RotateCcw, Search, ShieldCheck,
  ShieldOff, Undo2,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import {
  eximirUsuario,
  listarUsuarios,
  reiniciarSegundoFactor,
  revocarExencion,
  type PadronSegundoFactor,
  type UsuarioSegundoFactor,
} from '../../services/dosFactoresService';
import styles from './SeguridadAccesoPage.module.css';

const ETIQUETA_ROL: Record<string, string> = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  medico: 'Médico',
  visor: 'Visor',
  notificador: 'Notificador',
};

type Accion = 'eximir' | 'reiniciar' | 'revocar';

const TEXTO_ACCION: Record<Accion, {
  titulo: string; verbo: string; ayuda: string; minimo: number; peligro: boolean;
}> = {
  eximir: {
    titulo: 'Eximir del segundo paso',
    verbo: 'Eximir',
    ayuda: 'Esta persona podrá entrar solo con su contraseña o su código de correo. '
      + 'Úselo cuando no tenga un teléfono donde instalar la aplicación.',
    minimo: 10,
    peligro: true,
  },
  reiniciar: {
    titulo: 'Reiniciar el segundo paso',
    verbo: 'Reiniciar',
    ayuda: 'Se borra su configuración actual y tendrá que volver a escanear el código '
      + 'en su próximo ingreso. Es la salida cuando perdió el teléfono y sus códigos de respaldo.',
    minimo: 5,
    peligro: false,
  },
  revocar: {
    titulo: 'Retirar la exención',
    verbo: 'Retirar',
    ayuda: 'Volverá a exigírsele el segundo paso en su próximo ingreso.',
    minimo: 0,
    peligro: false,
  },
};

/** Cómo se ve una persona en el padrón: protegida, pendiente o exenta. */
function estadoDe(u: UsuarioSegundoFactor): { texto: string; tono: string } {
  if (u.exento) return { texto: 'Exento', tono: styles.tonoExento };
  if (u.activo) return { texto: 'Protegido', tono: styles.tonoOk };
  return { texto: 'Sin configurar', tono: styles.tonoPendiente };
}

const fecha = (ms: number | null) =>
  ms ? new Date(ms).toLocaleDateString('es-PA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function SeguridadAccesoPage() {
  const { addToast } = useToast();
  const [padron, setPadron] = useState<PadronSegundoFactor | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [dialogo, setDialogo] = useState<{ accion: Accion; usuario: UsuarioSegundoFactor } | null>(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      setPadron(await listarUsuarios());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar el padrón.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const puedeAdministrar = padron?.puedeAdministrar ?? false;
  const rolesSinExencion = padron?.rolesSinExencion ?? ['admin', 'supervisor'];

  const filtrados = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    if (!t) return padron?.usuarios ?? [];
    return (padron?.usuarios ?? []).filter((u) =>
      (u.nombre ?? '').toLowerCase().includes(t)
      || (u.email ?? '').toLowerCase().includes(t)
      || (ETIQUETA_ROL[u.rol] ?? u.rol).toLowerCase().includes(t));
  }, [padron, busqueda]);

  const resumen = useMemo(() => {
    const us = padron?.usuarios ?? [];
    return {
      total: us.length,
      protegidos: us.filter((u) => u.activo).length,
      pendientes: us.filter((u) => !u.activo && !u.exento).length,
      exentos: us.filter((u) => u.exento).length,
    };
  }, [padron]);

  const confirmar = async () => {
    if (!dialogo) return;
    const { accion, usuario } = dialogo;
    setEnviando(true);
    try {
      if (accion === 'eximir') await eximirUsuario(usuario.uid, motivo.trim());
      else if (accion === 'reiniciar') await reiniciarSegundoFactor(usuario.uid, motivo.trim());
      else await revocarExencion(usuario.uid);

      addToast('success', {
        eximir: `${usuario.nombre ?? usuario.email} quedó exento del segundo paso`,
        reiniciar: `Se reinició el segundo paso de ${usuario.nombre ?? usuario.email}`,
        revocar: `${usuario.nombre ?? usuario.email} vuelve a requerir segundo paso`,
      }[accion]);

      setDialogo(null);
      setMotivo('');
      await cargar();
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : 'No se pudo completar la operación.');
    } finally {
      setEnviando(false);
    }
  };

  const cfg = dialogo ? TEXTO_ACCION[dialogo.accion] : null;
  const motivoValido = !cfg || motivo.trim().length >= cfg.minimo;

  return (
    <MainLayout
      pageTitle="Seguridad de acceso"
      pageSubtitle="Segundo paso de verificación del personal del portal"
    >
      <div className={styles.pagina}>

        {/* Red de seguridad: si el backend alguna vez dejara de reconocer a este
            rol como administrador, la pantalla lo dice en vez de mostrar
            botones que van a fallar. Hoy no se dispara. */}
        {!cargando && !puedeAdministrar && (
          <div className={styles.avisoLectura}>
            <ShieldCheck size={16} aria-hidden="true" />
            <span>Está viendo el padrón en modo consulta: su rol no puede modificarlo.</span>
          </div>
        )}

        {/* Resumen */}
        <div className={styles.resumen}>
          <div className={styles.kpi}>
            <span className={styles.kpiValor}>{resumen.total}</span>
            <span className={styles.kpiEtiqueta}>cuentas del portal</span>
          </div>
          <div className={styles.kpi}>
            <span className={`${styles.kpiValor} ${styles.valorOk}`}>{resumen.protegidos}</span>
            <span className={styles.kpiEtiqueta}>con segundo paso</span>
          </div>
          <div className={styles.kpi}>
            <span className={`${styles.kpiValor} ${resumen.pendientes ? styles.valorAviso : ''}`}>
              {resumen.pendientes}
            </span>
            <span className={styles.kpiEtiqueta}>sin configurar</span>
            <span className={styles.kpiNota}>lo harán en su próximo ingreso</span>
          </div>
          <div className={styles.kpi}>
            <span className={`${styles.kpiValor} ${resumen.exentos ? styles.valorAlerta : ''}`}>
              {resumen.exentos}
            </span>
            <span className={styles.kpiEtiqueta}>exentos</span>
            <span className={styles.kpiNota}>entran con un solo factor</span>
          </div>
        </div>

        {/* Barra */}
        <div className={styles.barra}>
          <div className={styles.buscador}>
            <Search size={15} aria-hidden="true" />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, correo o rol…"
              aria-label="Buscar en el padrón"
            />
          </div>
          <Button variant="secondary" size="sm" onClick={cargar} disabled={cargando}>
            <RefreshCw size={15} className={cargando ? styles.girando : ''} />
            Actualizar
          </Button>
        </div>

        {/* Padrón */}
        {cargando ? (
          <div className={styles.estado}><Loader2 className={styles.girando} size={20} /> Cargando el padrón…</div>
        ) : error ? (
          <div className={styles.estadoError}><AlertTriangle size={18} /> {error}</div>
        ) : (
          <div className={styles.tarjetaTabla}>
            <div className={styles.scroll}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th>Persona</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Enrolado</th>
                    <th>Códigos de respaldo</th>
                    {puedeAdministrar && <th className={styles.thAcciones}>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((u) => {
                    const est = estadoDe(u);
                    const blindado = rolesSinExencion.includes(u.rol);
                    return (
                      <tr key={u.uid}>
                        <td>
                          <div className={styles.persona}>
                            <span className={styles.personaNombre}>{u.nombre ?? '(sin nombre)'}</span>
                            <span className={styles.personaCorreo}>{u.email ?? '—'}</span>
                          </div>
                        </td>
                        <td>
                          <span className={styles.rol}>{ETIQUETA_ROL[u.rol] ?? u.rol}</span>
                          {blindado && (
                            <span className={styles.blindado} title="Este rol no admite exención: administra el sistema">
                              obligatorio
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`${styles.pill} ${est.tono}`}>{est.texto}</span>
                          {u.exento && u.motivoExencion && (
                            <div className={styles.motivo} title={u.motivoExencion}>{u.motivoExencion}</div>
                          )}
                        </td>
                        <td className={styles.m}>{fecha(u.enroladoEn)}</td>
                        <td className={styles.m}>
                          {u.activo ? (
                            <span className={u.codigosRespaldoRestantes <= 2 ? styles.respaldoBajo : ''}>
                              {u.codigosRespaldoRestantes} de 8
                            </span>
                          ) : '—'}
                        </td>
                        {puedeAdministrar && (
                          <td className={styles.tdAcciones}>
                            {u.exento ? (
                              <button
                                type="button"
                                className={styles.accion}
                                onClick={() => { setDialogo({ accion: 'revocar', usuario: u }); setMotivo(''); }}
                              >
                                <Undo2 size={14} aria-hidden="true" /> Retirar exención
                              </button>
                            ) : (
                              <>
                                {u.activo && (
                                  <button
                                    type="button"
                                    className={styles.accion}
                                    onClick={() => { setDialogo({ accion: 'reiniciar', usuario: u }); setMotivo(''); }}
                                  >
                                    <RotateCcw size={14} aria-hidden="true" /> Reiniciar
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className={`${styles.accion} ${styles.accionRiesgo}`}
                                  disabled={blindado}
                                  title={blindado
                                    ? 'Este rol administra el sistema y no admite exención'
                                    : undefined}
                                  onClick={() => { setDialogo({ accion: 'eximir', usuario: u }); setMotivo(''); }}
                                >
                                  <ShieldOff size={14} aria-hidden="true" /> Eximir
                                </button>
                              </>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className={styles.pie}>
              {filtrados.length} de {resumen.total} cuentas ·
              {' '}Administrador y Supervisor administran esta pantalla y, por lo mismo, no admiten exención.
            </p>
          </div>
        )}
      </div>

      {/* Confirmación */}
      <Modal
        open={dialogo !== null}
        onClose={() => { if (!enviando) { setDialogo(null); setMotivo(''); } }}
        title={cfg?.titulo ?? ''}
        size="sm"
      >
        {dialogo && cfg && (
          <div className={styles.dialogo}>
            <p className={styles.dialogoPersona}>
              <KeyRound size={15} aria-hidden="true" />
              {dialogo.usuario.nombre ?? dialogo.usuario.email}
              <span>· {ETIQUETA_ROL[dialogo.usuario.rol] ?? dialogo.usuario.rol}</span>
            </p>
            <p className={styles.dialogoAyuda}>{cfg.ayuda}</p>

            {cfg.peligro && (
              <p className={styles.dialogoAlerta}>
                <AlertTriangle size={15} aria-hidden="true" />
                Mientras esté exenta, esta cuenta se protege solo con su contraseña. Queda
                registrado quién otorgó la exención y por qué.
              </p>
            )}

            {cfg.minimo > 0 && (
              <>
                <label className={styles.dialogoEtiqueta} htmlFor="motivo-2fa">
                  Motivo (mínimo {cfg.minimo} caracteres)
                </label>
                <textarea
                  id="motivo-2fa"
                  className={styles.dialogoTexto}
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  rows={3}
                  maxLength={300}
                  autoFocus
                  disabled={enviando}
                  placeholder={dialogo.accion === 'eximir'
                    ? 'Ej: no cuenta con teléfono inteligente institucional ni personal'
                    : 'Ej: extravió el teléfono y no conserva códigos de respaldo'}
                />
              </>
            )}

            <div className={styles.dialogoBotones}>
              <Button variant="ghost" size="sm" onClick={() => setDialogo(null)} disabled={enviando}>
                Cancelar
              </Button>
              <Button
                variant={cfg.peligro ? 'danger' : 'primary'}
                size="sm"
                loading={enviando}
                disabled={!motivoValido}
                onClick={confirmar}
              >
                {cfg.verbo}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </MainLayout>
  );
}
